import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert, Box, Button, IconButton, Link, Skeleton, Snackbar, Tooltip, Typography,
  Table, TableBody, TableCell, TableHead, TableRow, useMediaQuery, useTheme,
} from '@mui/material';
import {
  ArrowRight, CalendarCheck, CalendarClock, CalendarX2, ChevronLeft, ChevronRight,
  House, Inbox, Palmtree, Thermometer, TrendingUp, Users,
} from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import { useGetDepartmentsQuery, Department } from '../store/slices/departmentSlice';
import { useGetEmployeesQuery, Employee } from '../store/slices/employeeSlice';
import {
  useGetFilteredTimeOffEntriesQuery,
  useUpdateTimeOffEntryMutation,
  TimeOffType,
  TimeOffStatus,
  TimeOffEntry,
} from '../store/slices/timeOffSlice';
import { Link as RouterLink } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { isOwnRequestLocked, scopesEmployee } from '../utils/roles';
import {
  format, parseISO, differenceInDays, differenceInCalendarDays, isAfter, isBefore, isWithinInterval,
  startOfDay, endOfDay, startOfYear, endOfYear, addDays, subDays,
} from 'date-fns';
import {
  ConfirmDialog,
  CountBadge,
  EmptyState,
  ErrorState,
  KpiGrid,
  KpiTile,
  Meter,
  PageHeader,
  PersonCell,
  SectionCard,
  StatusPill,
  TableCard,
  TableEmptyRow,
  TableSkeletonRows,
  Toolbar,
  useDelayedFlag,
} from '../components/ui';
import { focusRing, sbg } from '../theme/sbgTokens';
import {
  formatCount, formatDate, formatDateRange, formatNumber, formatTime, MISSING,
} from '../utils/format';

/* ------------------------------------------------------------------------ */
/* Constants and pure helpers                                                */
/* ------------------------------------------------------------------------ */

// Stable fallbacks so memoized selectors don't recompute on every render
// while a query has no data yet.
const NO_DEPARTMENTS: Department[] = [];
const NO_EMPLOYEES: Employee[] = [];
const NO_ENTRIES: TimeOffEntry[] = [];

/** A pending request starting within this many days is flagged as urgent. */
const URGENT_DAYS = 3;
/** How far ahead the "Coming up" list looks. */
const UPCOMING_DAYS = 14;
/** Entries listed in Coming up; "View all time off" covers the rest. */
const UPCOMING_LIMIT = 8;
/** People listed in the usage table until it's expanded. */
const USAGE_LIMIT = 8;
/**
 * From this width the usage table and the two lists sit side by side (2:1).
 * Below it the 2fr column (~640px at 1280) is narrower than the usage table.
 */
const SPLIT_MEDIA = '@media (min-width: 1440px)';

const TYPE_META: Record<TimeOffType, { label: string; noun: string; Icon: LucideIcon }> = {
  [TimeOffType.PTO]: { label: 'PTO', noun: 'PTO', Icon: Palmtree },
  [TimeOffType.SICK]: { label: 'Sick', noun: 'sick time', Icon: Thermometer },
  [TimeOffType.WFH]: { label: 'WFH', noun: 'work-from-home', Icon: House },
};

const typeMeta = (type: string) =>
  TYPE_META[type as TimeOffType] ?? { label: type, noun: type.toLowerCase(), Icon: CalendarClock };

/** 8h · 4.5h — hours keep their decimal only when it's real. */
const formatHours = (hours: number | null | undefined) => {
  if (hours === null || hours === undefined || !Number.isFinite(hours)) return MISSING;
  return `${formatNumber(hours, Number.isInteger(hours) ? 0 : 1)}h`;
};

/** "Oct 12–14" for the current year (dense UI), the full range otherwise. */
const formatShortRange = (start: string, end: string) =>
  formatDateRange(start, end).replace(`, ${new Date().getFullYear()}`, '');

/** Relative start ("Starts in 2 days"): the Dates line already has the absolute date. */
const describeStart = (daysUntil: number) => {
  if (daysUntil < -1) return `Started ${formatNumber(-daysUntil)} days ago`;
  if (daysUntil === -1) return 'Started yesterday';
  if (daysUntil === 0) return 'Starts today';
  if (daysUntil === 1) return 'Starts tomorrow';
  return `Starts in ${formatNumber(daysUntil)} days`;
};

/** Hours used beyond an allotment; 0 when within it or there is none. */
const hoursOver = (used: number, allotment: number | null | undefined) =>
  allotment && allotment > 0 && used > allotment ? used - allotment : 0;

const firstNameOf = (name: string) => name.trim().split(/\s+/)[0] || name;

const describeDayAhead = (daysAhead: number) => {
  if (daysAhead <= 0) return 'Today';
  if (daysAhead === 1) return 'Tomorrow';
  return `In ${daysAhead} days`;
};

const requestName = (entry: TimeOffEntry) => `${entry.employee.name}’s ${typeMeta(entry.type).noun} request`;

type Decision = 'approve' | 'decline';

/** What a decision does, for the confirm dialog (book §11.14: state the consequence). */
const decisionConsequence = (entry: TimeOffEntry, action: Decision) => {
  const first = firstNameOf(entry.employee.name);
  const email = `${first} gets an email with your decision.`;
  if (action === 'decline') return `The request is marked declined and comes off the team calendar. ${email}`;
  if (entry.type === TimeOffType.WFH) return email;
  return `The ${formatHours(entry.hours)} count toward ${first}’s ${typeMeta(entry.type).noun} allotment. ${email}`;
};

/**
 * Why a decision didn't save, from the API error, then what to do. States a
 * cause only when the response tells us one (book §14.3).
 */
const describeDecisionFailure = (error: unknown) => {
  const { status, data } = (typeof error === 'object' && error !== null ? error : {}) as { status?: unknown; data?: unknown };
  const serverText =
    typeof data === 'object' && data !== null && typeof (data as { error?: unknown }).error === 'string'
      ? (data as { error: string }).error.trim()
      : '';
  const serverSentence = serverText && (/[.!?]$/.test(serverText) ? serverText : `${serverText}.`);
  const stillPending = 'The request is still pending.';
  if (status === 'FETCH_ERROR' || status === 'TIMEOUT_ERROR') {
    return `The connection dropped. ${stillPending} Try again in a minute.`;
  }
  if (status === 401) return `Your sign-in expired. ${stillPending} Reload the page to sign in again.`;
  if (status === 403) {
    return serverSentence
      ? `${serverSentence} ${stillPending}`
      : `You can’t decide requests for this person. ${stillPending} Ask HR to check your reporting line.`;
  }
  if (status === 409) {
    return serverSentence || 'This request changed since the page loaded. Refresh to see where it stands.';
  }
  if (typeof status === 'number' && status >= 400 && status < 500 && serverSentence) {
    return `${serverSentence} ${stillPending}`;
  }
  return `Something went wrong on our side. ${stillPending} Try again in a minute.`;
};

/** First column of a table that can scroll sideways: stays put so the row keeps its name. */
const stickyHeadCellSx = { position: 'sticky', left: 0, zIndex: 2, bgcolor: sbg.bg.surfaceSubtle } as const;
const stickyCellSx = {
  position: 'sticky',
  left: 0,
  zIndex: 1,
  bgcolor: sbg.bg.surface,
  '.MuiTableRow-root:hover > &': { bgcolor: sbg.bg.hover },
} as const;

// Entries (not rejected, non-zero hours) whose date range covers the given
// day, grouped by type. Powers both the stat cards (always today) and the
// Who's out panel (whichever day it is navigated to).
const groupEntriesActiveOn = (entries: TimeOffEntry[], date: Date) => {
  const active = entries.filter((e) => {
    if (e.status === 'REJECTED' || e.hours === 0) return false;
    try {
      return isWithinInterval(date, {
        start: startOfDay(parseISO(e.startDate)),
        end: endOfDay(parseISO(e.endDate)),
      });
    } catch {
      return false;
    }
  });
  return {
    pto: active.filter((e) => e.type === TimeOffType.PTO),
    sick: active.filter((e) => e.type === TimeOffType.SICK),
    wfh: active.filter((e) => e.type === TimeOffType.WFH),
    all: active,
  };
};

/* ------------------------------------------------------------------------ */
/* Small presentational pieces                                               */
/* ------------------------------------------------------------------------ */

/** An employee's name that opens their record. */
const PersonLink: React.FC<{ id: string; name: string }> = ({ id, name }) => (
  <Link
    component={RouterLink}
    to={`/employees/${id}`}
    underline="hover"
    sx={{
      color: sbg.text.primary,
      '&:hover': { color: sbg.text.primary },
      // The name sits in a clipped (noWrap) line, so the ring is drawn inside the link.
      '&.Mui-focusVisible, &:focus-visible': { outlineOffset: '-2px', borderRadius: '2px' },
    }}
  >
    {name}
  </Link>
);

/**
 * A pending request in the queue: dates, then type and hours, then when it
 * starts. Only a start within URGENT_DAYS gets a pill; otherwise it's plain text.
 */
const RequestDetails: React.FC<{ entry: TimeOffEntry; daysUntil: number }> = ({ entry, daysUntil }) => {
  const urgent = daysUntil <= URGENT_DAYS;
  const start = describeStart(daysUntil);
  return (
    <Box sx={{ display: 'grid', gap: 0.25, justifyItems: 'start', minWidth: 0 }}>
      <Box component="span" sx={{ color: sbg.text.primary, whiteSpace: 'nowrap', fontVariantNumeric: 'tabular-nums' }}>
        {formatShortRange(entry.startDate, entry.endDate)}
      </Box>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
        <Typography variant="body2" component="span" sx={{ color: sbg.text.secondary, whiteSpace: 'nowrap' }}>
          {typeMeta(entry.type).label} · {formatHours(entry.hours)}
          {!urgent && ` · ${start.toLowerCase()}`}
        </Typography>
        {urgent && <StatusPill tone="warning">{start}</StatusPill>}
      </Box>
    </Box>
  );
};

/** "96h / 88h" with a worded flag when the allotment is exceeded or used up. */
const UsedOf: React.FC<{ used: number; allotment: number | null | undefined }> = ({ used, allotment }) => {
  const over = hoursOver(used, allotment);
  const usedUp = over === 0 && Boolean(allotment) && used >= (allotment ?? 0);
  return (
    <Box sx={{ display: 'grid', gap: 0.25, justifyItems: 'end' }}>
      <Box component="span" sx={{ whiteSpace: 'nowrap' }}>
        {formatHours(used)} / {formatHours(allotment)}
      </Box>
      {over > 0 && (
        <Typography variant="body2" component="span" sx={{ color: sbg.status.warning.text, whiteSpace: 'nowrap' }}>
          {formatHours(over)} over
        </Typography>
      )}
      {usedUp && (
        <Typography variant="body2" component="span" sx={{ color: sbg.text.secondary, whiteSpace: 'nowrap' }}>
          Used up
        </Typography>
      )}
    </Box>
  );
};

/** Time-off type: a quiet icon and its name (a category, so no status color). */
const TypeLabel: React.FC<{ type: string }> = ({ type }) => {
  const { label, Icon } = typeMeta(type);
  return (
    <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.75, whiteSpace: 'nowrap', color: sbg.text.primary }}>
      <Box component="span" aria-hidden="true" sx={{ display: 'inline-flex', color: sbg.text.secondary }}>
        <Icon size={16} />
      </Box>
      {label}
    </Box>
  );
};

/** Heading row for a TableCard: card title, one-line description, trailing slot. */
const TableHeading: React.FC<{
  id: string;
  title: React.ReactNode;
  description?: React.ReactNode;
  end?: React.ReactNode;
  /** Makes the heading a programmatic focus target (tabIndex -1). */
  headingRef?: React.Ref<HTMLHeadingElement>;
}> = ({ id, title, description, end, headingRef }) => (
  <Toolbar end={end} sx={{ alignItems: 'flex-start', py: 0.5 }}>
    <Box sx={{ minWidth: 0 }}>
      <Typography
        id={id}
        ref={headingRef}
        tabIndex={headingRef ? -1 : undefined}
        variant="h4"
        component="h2"
        sx={{
          color: sbg.text.primary,
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          borderRadius: '4px',
          '&:focus-visible': focusRing,
        }}
      >
        {title}
      </Typography>
      {description && (
        <Typography variant="body2" component="p" sx={{ color: sbg.text.secondary, mt: 0.25 }}>
          {description}
        </Typography>
      )}
    </Box>
  </Toolbar>
);

/** A person row in a card list: avatar, linked name, a detail line, and a pending flag. */
const PersonRow: React.FC<{ entry: TimeOffEntry; secondary: React.ReactNode }> = ({ entry, secondary }) => (
  <Box
    component="li"
    sx={{
      display: 'flex',
      alignItems: 'center',
      gap: 1,
      minWidth: 0,
      py: 1,
      '& + li': { borderTop: `1px solid ${sbg.border.subtle}` },
    }}
  >
    <PersonCell
      name={<PersonLink id={entry.employee._id} name={entry.employee.name} />}
      avatarName={entry.employee.name}
      secondary={secondary}
      sx={{ flex: 1 }}
    />
    {entry.status === TimeOffStatus.PENDING && <StatusPill tone="warning">Pending approval</StatusPill>}
  </Box>
);

const listSx = { listStyle: 'none', m: 0, p: 0 } as const;

/** Skeleton rows for a card list, revealed after 300ms so fast loads never flash. */
const ListSkeleton: React.FC<{ label: string; rows?: number }> = ({ label, rows = 4 }) => {
  const visible = useDelayedFlag(true, 300);
  return (
    <Box role="status" aria-label={label} aria-busy="true" sx={{ display: 'grid', gap: 1.5, py: 1, visibility: visible ? 'visible' : 'hidden' }}>
      {Array.from({ length: rows }).map((_, index) => (
        <Box key={index} aria-hidden="true" sx={{ display: 'flex', alignItems: 'center', gap: 1.25 }}>
          <Skeleton variant="circular" width={32} height={32} />
          <Box sx={{ flex: 1, display: 'grid', gap: 0.75 }}>
            <Skeleton variant="rounded" height={12} width={`${50 + ((index * 17) % 30)}%`} />
            <Skeleton variant="rounded" height={10} width={`${28 + ((index * 11) % 24)}%`} />
          </Box>
        </Box>
      ))}
    </Box>
  );
};

/** Overline group label inside a card list ("Out · 2"). */
const GroupLabel: React.FC<{ children: React.ReactNode; count?: number }> = ({ children, count }) => (
  <Typography variant="overline" component="h3" sx={{ color: sbg.text.secondary, display: 'block', mt: 1.5, mb: 0.25 }}>
    {children}
    {typeof count === 'number' && ` · ${formatNumber(count)}`}
  </Typography>
);

/* ------------------------------------------------------------------------ */
/* Page                                                                      */
/* ------------------------------------------------------------------------ */

const ManagerDashboard: React.FC = () => {
  const theme = useTheme();
  // Below lg the queue's container is too narrow for its table (the sidebar
  // takes 240px from md), so requests render as a stacked list; KPI tiles go
  // two-up at the md size so the queue stays near the top on phones.
  const compact = useMediaQuery(theme.breakpoints.down('lg'), { noSsr: true });
  const phone = useMediaQuery(theme.breakpoints.down('sm'), { noSsr: true });
  const { profile, user } = useAuth();
  const {
    data: departmentsData,
    isLoading: isLoadingDepts,
    isError: isErrorDepts,
    refetch: refetchDepts,
  } = useGetDepartmentsQuery();
  const departments = departmentsData ?? NO_DEPARTMENTS;
  const {
    data: employeesData,
    isLoading: isLoadingEmployees,
    isError: isErrorEmployees,
    refetch: refetchEmployees,
  } = useGetEmployeesQuery();
  const allEmployees = employeesData ?? NO_EMPLOYEES;
  const employees = useMemo(
    () => allEmployees.filter((e) => !e.employmentStatus || e.employmentStatus === 'active'),
    [allEmployees]
  );
  // Fetch entries still active on/after Jan 1 (or 60 days back in early
  // January, so late-December pendings stay actionable). Bounding end_date
  // instead of start_date means a multi-month leave that began last year but
  // covers today still reaches "out today"/pending widgets, while every entry
  // the YTD stats need (started this year) trivially satisfies end >= start >= Jan 1.
  const timeOffActiveAfter = useMemo(() => {
    const now = new Date();
    const lookback = subDays(now, 60);
    const jan1 = startOfYear(now);
    return format(lookback < jan1 ? lookback : jan1, 'yyyy-MM-dd');
  }, []);
  const {
    data: timeOffData,
    isLoading: isLoadingTimeOff,
    isError: isErrorTimeOff,
    refetch: refetchTimeOff,
    fulfilledTimeStamp,
  } = useGetFilteredTimeOffEntriesQuery({
    endAfter: timeOffActiveAfter,
  });
  const timeOffEntries = timeOffData ?? NO_ENTRIES;
  const [updateTimeOffEntry] = useUpdateTimeOffEntryMutation();

  const [snackbar, setSnackbar] = useState<{ open: boolean; message: string; severity: 'success' | 'error' }>({
    open: false, message: '', severity: 'success',
  });
  const [confirmDialog, setConfirmDialog] = useState<{
    open: boolean;
    entry: TimeOffEntry | null;
    action: Decision;
  }>({ open: false, entry: null, action: 'approve' });
  const [confirming, setConfirming] = useState(false);
  const [showAllUsage, setShowAllUsage] = useState(false);

  // Focus targets for after a decision removes its row from the queue.
  const queueHeadingRef = useRef<HTMLHeadingElement>(null);
  const approveButtons = useRef(new Map<string, HTMLButtonElement>());
  const focusAfterDecision = useRef<{ id: string; index: number } | null>(null);
  const registerApproveButton = (id: string) => (node: HTMLButtonElement | null) => {
    if (node) approveButtons.current.set(id, node);
    else approveButtons.current.delete(id);
  };

  const isLoading = isLoadingDepts || isLoadingEmployees || isLoadingTimeOff;
  // A query that never returned data can't render the page; one that failed
  // to refresh still has its last good data, so show that with a notice.
  const loadFailed =
    !isLoading &&
    ((isErrorDepts && !departmentsData) || (isErrorEmployees && !employeesData) || (isErrorTimeOff && !timeOffData));
  const refreshFailed = !loadFailed && (isErrorDepts || isErrorEmployees || isErrorTimeOff);
  const retryAll = () => {
    refetchDepts();
    refetchEmployees();
    refetchTimeOff();
  };

  // Dates come from the calendar day, not the render, so the memos below
  // recompute only when the day or the data changes. Every comparison they
  // feed is day-granular, so midnight gives the same results as "now".
  const todayKey = format(new Date(), 'yyyy-MM-dd');
  const { todayStart, todayEnd, yearStart, yearEnd, upcomingEnd, upcomingEndKey } = useMemo(() => {
    const start = parseISO(todayKey);
    const lastUpcomingDay = addDays(start, UPCOMING_DAYS);
    return {
      todayStart: start,
      todayEnd: endOfDay(start),
      yearStart: startOfYear(start),
      yearEnd: endOfYear(start),
      // Inclusive: a request starting on day 14 counts as coming up.
      upcomingEnd: endOfDay(lastUpcomingDay),
      upcomingEndKey: format(lastUpcomingDay, 'yyyy-MM-dd'),
    };
  }, [todayKey]);
  const currentYear = todayStart.getFullYear();
  const daysPassed = differenceInDays(todayStart, yearStart);
  const daysInYear = differenceInDays(yearEnd, yearStart) + 1;

  // Day shown in the Who's out panel: 0 = today, 1 = tomorrow, -1 = yesterday.
  const [teamDayOffset, setTeamDayOffset] = useState(0);
  const teamViewDate = useMemo(() => addDays(todayStart, teamDayOffset), [todayStart, teamDayOffset]);
  // Entries before this date were never fetched, so block navigating past it.
  const canGoPrevDay = differenceInDays(teamViewDate, parseISO(timeOffActiveAfter)) > 0;
  const teamDayLabel =
    teamDayOffset === 0 ? 'Today' :
    teamDayOffset === 1 ? 'Tomorrow' :
    teamDayOffset === -1 ? 'Yesterday' :
    format(teamViewDate, 'EEE');
  const teamDayPhrase =
    teamDayOffset === 0 ? 'today' :
    teamDayOffset === 1 ? 'tomorrow' :
    teamDayOffset === -1 ? 'yesterday' :
    `on ${format(teamViewDate, 'EEEE, MMM d')}`;
  const teamDayDescription =
    Math.abs(teamDayOffset) <= 1
      ? `${teamDayLabel} · ${format(teamViewDate, 'EEE, MMM d')}`
      : format(teamViewDate, 'EEEE, MMM d');

  const myEmployee = useMemo(
    () => employees.find((employee) => employee.userId === user?.id),
    [employees, user?.id]
  );

  const managerDepartmentIds = useMemo(
    () => profile?.role === 'manager' && user?.id
      ? departments
          .filter((department) => department.managerUserId === user.id)
          .map((department) => department._id)
      : [],
    [departments, profile?.role, user?.id]
  );

  // ── My Team ──
  const myTeam = useMemo(() => {
    if (!user?.id) return [];
    return employees.filter((employee) => scopesEmployee(employee, {
      role: profile?.role,
      userId: user.id,
      managerName: myEmployee?.name,
      managerDepartmentIds,
    }));
  }, [employees, managerDepartmentIds, myEmployee?.name, profile?.role, user?.id]);

  const myDepartments = useMemo(() => {
    if (profile?.role !== 'manager' || !user?.id) return [];
    return departments.filter((dept) => dept.managerUserId === user.id);
  }, [departments, profile?.role, user?.id]);

  const myTeamIds = useMemo(() => new Set(myTeam.map((e) => e._id)), [myTeam]);

  // ── Team Time Off Entries ──
  const teamEntries = useMemo(() => {
    return timeOffEntries.filter((e) => myTeamIds.has(e.employee._id));
  }, [timeOffEntries, myTeamIds]);

  // ── Who's out today from my team (drives the KPI tiles) ──
  const outToday = useMemo(() => groupEntriesActiveOn(teamEntries, todayStart), [teamEntries, todayStart]);

  // ── Who's out on the day the Who's out panel is set to ──
  const outOnViewDay = useMemo(
    () => groupEntriesActiveOn(teamEntries, teamViewDate),
    [teamEntries, teamViewDate]
  );

  // ── Pending requests for me to approve ──
  // Never my own, which my manager or HR decides, even when I'm in a
  // department I run.
  const pendingRequests = useMemo(() => {
    return teamEntries
      .filter((e) => e.status === TimeOffStatus.PENDING && e.hours > 0)
      .filter((e) => !isOwnRequestLocked(e.employee, { role: profile?.role, userId: user?.id }))
      .sort((a, b) => new Date(a.startDate).getTime() - new Date(b.startDate).getTime());
  }, [teamEntries, profile?.role, user?.id]);

  // ── Upcoming time off (next 14 days) ──
  const upcomingTimeOff = useMemo(() => {
    return teamEntries
      .filter((e) => {
        if (e.status === 'REJECTED') return false;
        const start = parseISO(e.startDate);
        return isAfter(start, todayEnd) && isBefore(start, upcomingEnd);
      })
      .sort((a, b) => new Date(a.startDate).getTime() - new Date(b.startDate).getTime());
  }, [teamEntries, todayEnd, upcomingEnd]);

  // ── Team pacing stats ──
  const teamPacing = useMemo(() => {
    return myTeam.map((employee) => {
      const empEntries = teamEntries.filter(
        (e) =>
          e.employee._id === employee._id &&
          e.status === 'APPROVED' &&
          isAfter(parseISO(e.startDate), yearStart) &&
          isBefore(parseISO(e.startDate), yearEnd) &&
          !isAfter(parseISO(e.startDate), todayStart)
      );
      const ptoUsed = empEntries
        .filter((e) => e.type === TimeOffType.PTO)
        .reduce((s, e) => s + e.hours, 0);
      const sickUsed = empEntries
        .filter((e) => e.type === TimeOffType.SICK)
        .reduce((s, e) => s + e.hours, 0);
      const totalUsed = ptoUsed + sickUsed;
      const totalAllotment = (employee.ptoAllotment || 0) + (employee.sickTimeAllotment || 0);
      const totalPace = daysPassed > 0 ? Math.round((totalUsed / daysPassed) * daysInYear) : 0;
      const isOverPace = totalAllotment > 0 && totalPace > totalAllotment;
      const totalPercent = totalAllotment > 0 ? Math.round((totalUsed / totalAllotment) * 100) : 0;

      return {
        ...employee,
        ptoUsed,
        sickUsed,
        totalUsed,
        totalAllotment,
        totalPace,
        isOverPace,
        totalPercent,
        ptoPercent: employee.ptoAllotment > 0 ? Math.round((ptoUsed / employee.ptoAllotment) * 100) : 0,
      };
    });
  }, [myTeam, teamEntries, yearStart, yearEnd, todayStart, daysPassed, daysInYear]);

  const overPaceCount = teamPacing.filter((e) => e.isOverPace).length;

  // ── Presentation-only derivations ──
  const pendingRows = useMemo(
    () => pendingRequests.map((entry) => ({
      entry,
      daysUntil: differenceInCalendarDays(parseISO(entry.startDate), todayStart),
    })),
    [pendingRequests, todayStart]
  );
  const urgentCount = pendingRows.filter((row) => row.daysUntil <= URGENT_DAYS).length;
  // Anyone flagged (over pace, or past a PTO or sick allotment) first, then
  // by share used, so the collapsed table never hides someone who needs a look.
  const usageRows = useMemo(
    () => teamPacing
      .map((emp) => {
        const ptoOver = hoursOver(emp.ptoUsed, emp.ptoAllotment);
        const sickOver = hoursOver(emp.sickUsed, emp.sickTimeAllotment);
        return { ...emp, ptoOver, sickOver, flagged: emp.isOverPace || ptoOver > 0 || sickOver > 0 };
      })
      .sort((a, b) => Number(b.flagged) - Number(a.flagged) || b.totalPercent - a.totalPercent),
    [teamPacing]
  );
  const visibleUsageRows = showAllUsage ? usageRows : usageRows.slice(0, USAGE_LIMIT);
  const upcomingByDay = useMemo(() => {
    const groups: { day: string; entries: TimeOffEntry[] }[] = [];
    upcomingTimeOff.slice(0, UPCOMING_LIMIT).forEach((entry) => {
      const day = entry.startDate.slice(0, 10);
      const last = groups[groups.length - 1];
      if (last && last.day === day) last.entries.push(entry);
      else groups.push({ day, entries: [entry] });
    });
    return groups;
  }, [upcomingTimeOff]);

  const teamSize = myTeam.length;
  const teamSizeLabel = formatCount(teamSize, 'person', 'people');
  const outTodayCount = outToday.pto.length + outToday.sick.length;
  const outOnViewDayEntries = [...outOnViewDay.pto, ...outOnViewDay.sick];
  const availableOnViewDay = Math.max(0, teamSize - outOnViewDayEntries.length);
  const noTeam = !isLoading && !loadFailed && teamSize === 0;
  const updatedAt = fulfilledTimeStamp ? formatTime(fulfilledTimeStamp) : null;

  // ── Handlers ──
  /** Approves or declines one request; resolves true when it saved. */
  const decide = async (entry: TimeOffEntry, action: Decision) => {
    try {
      await updateTimeOffEntry({
        id: entry._id,
        timeOff: { status: action === 'approve' ? TimeOffStatus.APPROVED : TimeOffStatus.REJECTED } as any,
      }).unwrap();
      setSnackbar({
        open: true,
        message: `${action === 'approve' ? 'Approved' : 'Declined'} ${requestName(entry)} for ${formatShortRange(entry.startDate, entry.endDate)}.`,
        severity: 'success',
      });
      return true;
    } catch (error) {
      setSnackbar({
        open: true,
        message: `Couldn’t ${action} ${requestName(entry)}. ${describeDecisionFailure(error)}`,
        severity: 'error',
      });
      return false;
    }
  };

  const openConfirm = (entry: TimeOffEntry, action: Decision) => {
    focusAfterDecision.current = null;
    setConfirmDialog({ open: true, entry, action });
  };

  const closeConfirm = () => setConfirmDialog((dialog) => ({ ...dialog, open: false }));

  const confirmAction = async () => {
    const { entry, action } = confirmDialog;
    if (!entry) return;
    setConfirming(true);
    const index = pendingRows.findIndex((row) => row.entry._id === entry._id);
    const saved = await decide(entry, action);
    // The decided row leaves the queue when the list refreshes; remember its
    // place so focus can move to the next request rather than the page top.
    if (saved && index >= 0) focusAfterDecision.current = { id: entry._id, index };
    setConfirming(false);
    closeConfirm();
  };

  // Once the decided row is gone (and the dialog has closed), put focus on the
  // next request's Approve button, or the queue heading when none is left.
  // Only when focus was lost with the row, never pulled from somewhere else.
  useEffect(() => {
    const target = focusAfterDecision.current;
    if (!target || confirmDialog.open) return;
    if (pendingRows.some((row) => row.entry._id === target.id)) return;
    focusAfterDecision.current = null;
    const active = document.activeElement;
    const focusLost =
      !active || active === document.body || !active.isConnected || active.closest('[role="dialog"]') !== null;
    if (!focusLost) return;
    const next = pendingRows[target.index];
    const nextButton = next ? approveButtons.current.get(next.entry._id) : undefined;
    (nextButton ?? queueHeadingRef.current)?.focus();
  }, [pendingRows, confirmDialog.open]);

  const closeSnackbar = () => setSnackbar((s) => ({ ...s, open: false }));

  // Two-up tiles are ~170px wide on a phone: drop the icon so the label fits.
  const kpiSize = compact ? 'md' : 'lg';
  const kpiIcon = (icon: React.ReactNode) => (phone ? undefined : icon);

  // Layout, in DOM (and tab) order: queue, Who's out, Coming up, usage.
  // One column on phones. From lg the queue and usage span both columns with
  // the two lists side by side between them. From 1440px the queue stays full
  // width and the usage table (2fr) sits beside the stacked lists (1fr); the
  // last row is 1fr so a tall usage table doesn't open a gap between lists.
  const layoutSx = {
    display: 'grid',
    gap: 3,
    alignItems: 'start',
    gridTemplateColumns: { xs: 'minmax(0, 1fr)', lg: 'repeat(2, minmax(0, 1fr))' },
    [SPLIT_MEDIA]: { gridTemplateColumns: 'minmax(0, 2fr) minmax(0, 1fr)', gridTemplateRows: 'auto auto 1fr' },
  };
  const fullRowSx = { gridColumn: { lg: '1 / -1' }, minWidth: 0 };

  const header = (
    <PageHeader
      overline="Dashboard"
      title="Team overview"
      meta={[
        myDepartments.length > 0 ? myDepartments.map((d) => d.name).join(', ') : 'Your team',
        !isLoading && !loadFailed ? teamSizeLabel : null,
        formatDate(todayKey, { weekday: true }),
        updatedAt ? `Updated ${updatedAt}` : null,
      ]}
      actions={
        <>
          <Button component={RouterLink} to="/team-attendance" variant="outlined" startIcon={<Users size={16} />}>
            Team attendance
          </Button>
          <Button component={RouterLink} to="/time-off" variant="contained">
            Manage requests
          </Button>
        </>
      }
    />
  );

  /* ── Waiting on you: the approval queue ── */
  const pendingCount = pendingRequests.length;

  const noteOf = (entry: TimeOffEntry) => entry.notes || entry.originalCalendarSubject;

  // Decline (ghost) and Approve (secondary): the queue keeps one primary, the header's.
  const decisionButtons = (entry: TimeOffEntry, stacked: boolean) => (
    <Box
      sx={
        stacked
          ? { display: 'grid', gridTemplateColumns: { xs: 'repeat(2, minmax(0, 1fr))', sm: 'repeat(2, auto)' }, gap: 1 }
          : { display: 'inline-flex', gap: 1, justifyContent: 'flex-end', whiteSpace: 'nowrap' }
      }
    >
      <Button
        size={stacked ? 'medium' : 'small'}
        variant="text"
        aria-label={`Decline ${requestName(entry)}`}
        onClick={() => openConfirm(entry, 'decline')}
      >
        Decline
      </Button>
      <Button
        ref={registerApproveButton(entry._id)}
        size={stacked ? 'medium' : 'small'}
        variant="outlined"
        aria-label={`Approve ${requestName(entry)}`}
        onClick={() => openConfirm(entry, 'approve')}
      >
        Approve
      </Button>
    </Box>
  );

  const queueEmptyTitle = 'Nothing waiting on you';
  const queueEmptyBody = 'New time-off requests from your team appear here for your approval.';

  const queueTable = (
    <Table sx={{ minWidth: 560 }}>
      <TableHead>
        <TableRow>
          <TableCell sx={stickyHeadCellSx}>Employee</TableCell>
          <TableCell>Request</TableCell>
          <TableCell align="right">
            <span className="sbg-sr-only">Actions</span>
          </TableCell>
        </TableRow>
      </TableHead>
      <TableBody>
        {isLoading ? (
          <TableSkeletonRows columns={3} rows={3} />
        ) : pendingRows.length === 0 ? (
          <TableEmptyRow colSpan={3} title={queueEmptyTitle} body={queueEmptyBody} />
        ) : (
          pendingRows.map(({ entry, daysUntil }) => {
            const note = noteOf(entry);
            return (
              <TableRow key={entry._id} hover>
                <TableCell sx={stickyCellSx}>
                  <PersonCell
                    name={<PersonLink id={entry.employee._id} name={entry.employee.name} />}
                    avatarName={entry.employee.name}
                    secondary={
                      note ? (
                        <Tooltip title={note}>
                          <span>{note}</span>
                        </Tooltip>
                      ) : undefined
                    }
                    sx={{ maxWidth: 240 }}
                  />
                </TableCell>
                <TableCell>
                  <RequestDetails entry={entry} daysUntil={daysUntil} />
                </TableCell>
                <TableCell align="right">{decisionButtons(entry, false)}</TableCell>
              </TableRow>
            );
          })
        )}
      </TableBody>
    </Table>
  );

  // Below lg: one stacked item per request, actions under it (full width on phones).
  const queueList = isLoading ? (
    <Box sx={{ px: 2 }}>
      <ListSkeleton label="Loading requests waiting on you" rows={3} />
    </Box>
  ) : pendingRows.length === 0 ? (
    <EmptyState compact title={queueEmptyTitle} body={queueEmptyBody} />
  ) : (
    <Box component="ul" sx={listSx}>
      {pendingRows.map(({ entry, daysUntil }) => {
        const note = noteOf(entry);
        return (
          <Box
            component="li"
            key={entry._id}
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: 'minmax(0, 1fr)', sm: 'minmax(0, 1fr) auto' },
              alignItems: 'center',
              gap: 1.5,
              px: 2,
              py: 1.5,
              '& + li': { borderTop: `1px solid ${sbg.border.subtle}` },
            }}
          >
            <Box sx={{ display: 'grid', gap: 1, minWidth: 0 }}>
              <PersonCell
                name={<PersonLink id={entry.employee._id} name={entry.employee.name} />}
                avatarName={entry.employee.name}
                secondary={note || undefined}
              />
              {/* Indented to line up with the name (32px avatar + 10px gap). */}
              <Box sx={{ pl: '42px', minWidth: 0 }}>
                <RequestDetails entry={entry} daysUntil={daysUntil} />
              </Box>
            </Box>
            {decisionButtons(entry, true)}
          </Box>
        );
      })}
    </Box>
  );

  const pendingCard = (
    <TableCard
      aria-label="Waiting on you"
      sx={fullRowSx}
      toolbar={
        <TableHeading
          id="manager-pending-title"
          headingRef={queueHeadingRef}
          title={
            <>
              Waiting on you
              {!isLoading && pendingCount > 0 && (
                <>
                  <Box component="span" aria-hidden="true" sx={{ display: 'inline-flex' }}>
                    <CountBadge alert count={pendingCount} />
                  </Box>
                  <span className="sbg-sr-only">, {formatCount(pendingCount, 'request')}</span>
                </>
              )}
            </>
          }
          description="Time-off requests from your team, soonest start first"
        />
      }
    >
      {compact ? queueList : queueTable}
    </TableCard>
  );

  /* ── Who's out (navigable by day) ── */
  const dayNav = (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
      <Tooltip title={canGoPrevDay ? 'Previous day' : 'Earlier days aren’t loaded on the dashboard'}>
        <span>
          <IconButton
            size="small"
            aria-label="Previous day"
            onClick={() => setTeamDayOffset((offset) => offset - 1)}
            disabled={!canGoPrevDay}
          >
            <ChevronLeft size={18} />
          </IconButton>
        </span>
      </Tooltip>
      {teamDayOffset !== 0 && (
        <Button size="small" variant="text" onClick={() => setTeamDayOffset(0)}>
          Today
        </Button>
      )}
      <Tooltip title="Next day">
        <IconButton
          size="small"
          aria-label="Next day"
          onClick={() => setTeamDayOffset((offset) => offset + 1)}
        >
          <ChevronRight size={18} />
        </IconButton>
      </Tooltip>
    </Box>
  );

  const outLine = (entry: TimeOffEntry, withType: boolean) => {
    // A multi-day request's hours cover the whole request, not the day shown.
    const multiDay = entry.startDate.slice(0, 10) !== entry.endDate.slice(0, 10);
    const parts = [
      withType ? typeMeta(entry.type).label : null,
      multiDay ? `${formatHours(entry.hours)} total` : formatHours(entry.hours),
      multiDay ? `through ${formatDate(entry.endDate, { short: true })}` : null,
    ].filter(Boolean);
    return parts.join(' · ');
  };

  const whosOutCard = (
    <SectionCard
      dense
      id="manager-whos-out"
      title="Who’s out"
      description={teamDayDescription}
      action={dayNav}
      sx={{ minWidth: 0, [SPLIT_MEDIA]: { gridColumn: 2, gridRow: 2 } }}
    >
      {isLoading ? (
        <ListSkeleton label="Loading who’s out" rows={3} />
      ) : (
        <>
          <Box sx={{ display: 'grid', gap: 1, pb: 2, borderBottom: `1px solid ${sbg.border.subtle}` }}>
            <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1, flexWrap: 'wrap' }}>
              <Typography variant="numericMd" component="p" sx={{ color: sbg.text.primary }}>
                {formatNumber(availableOnViewDay)}
              </Typography>
              <Typography variant="body1" component="p" sx={{ color: sbg.text.secondary }}>
                of {teamSizeLabel} available {teamDayPhrase}
              </Typography>
            </Box>
            <Meter
              value={availableOnViewDay}
              max={Math.max(teamSize, 1)}
              aria-label={`${formatNumber(availableOnViewDay)} of ${teamSizeLabel} available ${teamDayPhrase}`}
            />
            <Typography variant="body2" component="p" sx={{ color: sbg.text.secondary }}>
              {formatNumber(outOnViewDayEntries.length)} out · {formatNumber(outOnViewDay.wfh.length)} working from home
            </Typography>
          </Box>

          {outOnViewDay.all.length === 0 ? (
            <EmptyState
              compact
              icon={<CalendarCheck />}
              title={`No one is out ${teamDayPhrase}`}
              body="Time off and work-from-home days for your team show up here."
            />
          ) : (
            <>
              {outOnViewDayEntries.length > 0 && (
                <>
                  <GroupLabel count={outOnViewDayEntries.length}>Out</GroupLabel>
                  <Box component="ul" sx={listSx}>
                    {outOnViewDayEntries.map((entry) => (
                      <PersonRow key={entry._id} entry={entry} secondary={outLine(entry, true)} />
                    ))}
                  </Box>
                </>
              )}
              {outOnViewDay.wfh.length > 0 && (
                <>
                  <GroupLabel count={outOnViewDay.wfh.length}>Working from home</GroupLabel>
                  <Box component="ul" sx={listSx}>
                    {outOnViewDay.wfh.map((entry) => (
                      <PersonRow key={entry._id} entry={entry} secondary={outLine(entry, false)} />
                    ))}
                  </Box>
                </>
              )}
            </>
          )}
        </>
      )}
    </SectionCard>
  );

  /* ── Coming up (next 14 days) ── */
  const upcomingHidden = upcomingTimeOff.length - UPCOMING_LIMIT;
  const upcomingCard = (
    <SectionCard
      dense
      id="manager-coming-up"
      title="Coming up"
      description={`Time off and WFH days starting in the next ${UPCOMING_DAYS} days`}
      footer={
        <>
          {!isLoading && upcomingHidden > 0 && (
            <Typography variant="body2" component="p" sx={{ color: sbg.text.secondary }}>
              Showing {formatNumber(UPCOMING_LIMIT)} of {formatNumber(upcomingTimeOff.length)}
            </Typography>
          )}
          <Button component={RouterLink} to="/time-off" size="small" variant="text" endIcon={<ArrowRight size={16} />}>
            View all time off
          </Button>
        </>
      }
      sx={{ minWidth: 0, [SPLIT_MEDIA]: { gridColumn: 2, gridRow: 3 } }}
    >
      {isLoading ? (
        <ListSkeleton label="Loading upcoming time off" />
      ) : upcomingByDay.length === 0 ? (
        <EmptyState
          compact
          icon={<CalendarClock />}
          title={`Nothing scheduled in the next ${UPCOMING_DAYS} days`}
          body={`Time off and WFH days from your team that start by ${formatDate(upcomingEndKey, { short: true })} show up here.`}
        />
      ) : (
        <Box component="ol" aria-label="Upcoming time off by start date" sx={listSx}>
          {upcomingByDay.map((group, index) => (
            <Box component="li" key={group.day} sx={{ pt: index === 0 ? 0 : 1.5 }}>
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'baseline',
                  justifyContent: 'space-between',
                  gap: 1,
                  pb: 0.5,
                  borderBottom: `1px solid ${sbg.border.default}`,
                }}
              >
                <Typography variant="labelSm" component="h3" sx={{ color: sbg.text.primary }}>
                  {formatDate(group.day, { short: true, weekday: true })}
                </Typography>
                <Typography variant="body2" component="span" sx={{ color: sbg.text.secondary }}>
                  {describeDayAhead(differenceInCalendarDays(parseISO(group.day), todayStart))}
                </Typography>
              </Box>
              <Box component="ul" sx={listSx}>
                {group.entries.map((entry) => (
                  <PersonRow
                    key={entry._id}
                    entry={entry}
                    secondary={`${typeMeta(entry.type).label} · ${formatShortRange(entry.startDate, entry.endDate)} · ${formatHours(entry.hours)}`}
                  />
                ))}
              </Box>
            </Box>
          ))}
        </Box>
      )}
    </SectionCard>
  );

  /* ── Time off used this year ── */
  type UsageRow = (typeof usageRows)[number];

  const quietStatus = (text: string) => (
    <Typography variant="body2" component="span" sx={{ color: sbg.text.secondary, whiteSpace: 'nowrap' }}>
      {text}
    </Typography>
  );

  // Most urgent state first. "On pace" only when nothing in the row is flagged.
  const paceStatus = (emp: UsageRow) => {
    if (emp.totalAllotment === 0) return quietStatus('No allotment');
    if (emp.isOverPace) {
      return (
        <StatusPill
          tone="warning"
          title={`Projected ${formatHours(emp.totalPace)} against a ${formatHours(emp.totalAllotment)} allotment`}
        >
          Over pace
        </StatusPill>
      );
    }
    if (emp.ptoOver > 0) return <StatusPill tone="warning">Over PTO</StatusPill>;
    if (emp.sickOver > 0) return <StatusPill tone="warning">Over sick time</StatusPill>;
    if (emp.ptoAllotment > 0 && emp.ptoUsed >= emp.ptoAllotment) return quietStatus('PTO used up');
    return quietStatus('On pace');
  };

  const usageCount = usageRows.length;
  const usageFooter = !isLoading && usageCount > USAGE_LIMIT ? (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 1,
        flexWrap: 'wrap',
        px: 2,
        py: 1,
        borderTop: `1px solid ${sbg.border.subtle}`,
      }}
    >
      <Typography variant="body2" component="p" sx={{ color: sbg.text.secondary }}>
        {showAllUsage
          ? `Showing all ${formatCount(usageCount, 'person', 'people')}`
          : `Showing ${formatNumber(USAGE_LIMIT)} of ${formatCount(usageCount, 'person', 'people')}`}
      </Typography>
      <Button
        size="small"
        variant="text"
        aria-expanded={showAllUsage}
        aria-controls="manager-usage-table"
        onClick={() => setShowAllUsage((value) => !value)}
      >
        {showAllUsage ? 'Show fewer' : `Show all ${formatNumber(usageCount)}`}
      </Button>
    </Box>
  ) : undefined;

  const usageCard = (
    <TableCard
      aria-label="Time off used this year"
      sx={{ ...fullRowSx, [SPLIT_MEDIA]: { gridColumn: 1, gridRow: '2 / span 2' } }}
      toolbar={
        <TableHeading
          id="manager-usage-title"
          title="Time off used this year"
          description="Approved PTO and sick hours to date. Projected assumes the same pace through Dec 31."
          end={
            !isLoading && overPaceCount > 0 ? (
              <StatusPill tone="warning">{formatCount(overPaceCount, 'person', 'people')} over pace</StatusPill>
            ) : null
          }
        />
      }
      footer={usageFooter}
    >
      <Table id="manager-usage-table" sx={{ minWidth: 600 }}>
        <TableHead>
          <TableRow>
            <TableCell sx={stickyHeadCellSx}>Employee</TableCell>
            <TableCell align="right">PTO used</TableCell>
            <TableCell align="right">Sick used</TableCell>
            <TableCell align="right">Total used</TableCell>
            <TableCell>Pace</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {isLoading ? (
            <TableSkeletonRows columns={5} rows={5} />
          ) : (
            visibleUsageRows.map((emp) => (
              <TableRow key={emp._id} hover>
                <TableCell sx={stickyCellSx}>
                  <PersonCell
                    name={<PersonLink id={emp._id} name={emp.name} />}
                    avatarName={emp.name}
                    secondary={emp.department?.name || undefined}
                    sx={{ maxWidth: 200 }}
                  />
                </TableCell>
                <TableCell align="right">
                  <UsedOf used={emp.ptoUsed} allotment={emp.ptoAllotment} />
                </TableCell>
                <TableCell align="right">
                  <UsedOf used={emp.sickUsed} allotment={emp.sickTimeAllotment} />
                </TableCell>
                <TableCell align="right">
                  <Box sx={{ display: 'grid', gap: 0.75, justifyItems: 'end', width: 120, ml: 'auto' }}>
                    <Box component="span" sx={{ whiteSpace: 'nowrap' }}>
                      {formatHours(emp.totalUsed)} / {formatHours(emp.totalAllotment)}
                    </Box>
                    <Meter
                      value={emp.totalUsed}
                      max={emp.totalAllotment}
                      tone={emp.isOverPace ? 'warning' : 'brand'}
                      aria-label={`${emp.name}: ${formatHours(emp.totalUsed)} of ${formatHours(emp.totalAllotment)} used`}
                      sx={{ width: '100%' }}
                    />
                  </Box>
                </TableCell>
                <TableCell>
                  <Box sx={{ display: 'grid', gap: 0.25, justifyItems: 'start' }}>
                    {paceStatus(emp)}
                    <Typography
                      variant="body2"
                      component="span"
                      sx={{ color: sbg.text.secondary, whiteSpace: 'nowrap', fontVariantNumeric: 'tabular-nums' }}
                    >
                      Projected {formatHours(emp.totalPace)}
                    </Typography>
                  </Box>
                </TableCell>
              </TableRow>
            ))
          )}
        </TableBody>
      </Table>
    </TableCard>
  );

  const confirmEntry = confirmDialog.entry;
  const confirmNote = confirmEntry ? noteOf(confirmEntry) : undefined;
  // Name the failing data: the whole team when people or departments didn't load.
  const teamFailed = (isErrorDepts && !departmentsData) || (isErrorEmployees && !employeesData);

  return (
    <Box>
      {header}

      {loadFailed ? (
        <SectionCard>
          <ErrorState
            title={teamFailed ? 'Couldn’t load your team' : 'Couldn’t load your team’s time off'}
            body="The data didn’t come through, so there’s nothing to show yet. Try again in a minute."
            onRetry={retryAll}
          />
        </SectionCard>
      ) : noTeam ? (
        <SectionCard>
          <EmptyState
            icon={<Users />}
            title="No one is assigned to you yet"
            body="Your team appears here once HR assigns people to you. Ask an HR admin to update reporting lines in Employees."
          />
        </SectionCard>
      ) : (
        <>
          {refreshFailed && (
            <Alert
              severity="warning"
              sx={{ mb: 3 }}
              action={
                <Button size="small" variant="text" onClick={retryAll}>
                  Try again
                </Button>
              }
            >
              Couldn’t refresh your team’s data. Showing what loaded{updatedAt ? ` at ${updatedAt}` : ' earlier'}.
            </Alert>
          )}

          {/* Two-up below lg so four tiles don't push the queue off a phone's first screen. */}
          <KpiGrid phoneColumns={2} sx={{ gap: { xs: 1.5, sm: 2 } }}>
            <KpiTile
              label="Out today"
              icon={kpiIcon(<CalendarX2 />)}
              value={formatNumber(outTodayCount)}
              caption={`of ${teamSizeLabel} · PTO and sick`}
              size={kpiSize}
              loading={isLoading}
            />
            <KpiTile
              label="WFH today"
              icon={kpiIcon(<House />)}
              value={formatNumber(outToday.wfh.length)}
              caption={`of ${teamSizeLabel}`}
              size={kpiSize}
              loading={isLoading}
            />
            <KpiTile
              label="Waiting on you"
              icon={kpiIcon(<Inbox />)}
              size={kpiSize}
              value={formatNumber(pendingRequests.length)}
              caption={
                urgentCount > 0
                  ? `${formatNumber(urgentCount)} start within ${URGENT_DAYS} days`
                  : pendingRequests.length > 0
                    ? `None start in the next ${URGENT_DAYS} days`
                    : 'Nothing to review'
              }
              loading={isLoading}
            />
            <KpiTile
              label="Over pace"
              icon={kpiIcon(<TrendingUp />)}
              size={kpiSize}
              value={formatNumber(overPaceCount)}
              caption={`of ${teamSizeLabel} · ${currentYear} to date`}
              info="People whose PTO and sick hours so far, projected to Dec 31 at the same pace, exceed their allotment."
              loading={isLoading}
            />
          </KpiGrid>

          <Box sx={layoutSx}>
            {pendingCard}
            {whosOutCard}
            {upcomingCard}
            {usageCard}
          </Box>
        </>
      )}

      {/* ── Approve / decline confirmation ── */}
      <ConfirmDialog
        open={confirmDialog.open}
        onClose={closeConfirm}
        onConfirm={confirmAction}
        title={
          confirmEntry
            ? `${confirmDialog.action === 'approve' ? 'Approve' : 'Decline'} ${requestName(confirmEntry)}?`
            : ''
        }
        confirmLabel={confirmDialog.action === 'approve' ? 'Approve request' : 'Decline request'}
        loadingLabel={confirmDialog.action === 'approve' ? 'Approving…' : 'Declining…'}
        body={confirmEntry ? decisionConsequence(confirmEntry, confirmDialog.action) : undefined}
        destructive={confirmDialog.action === 'decline'}
        loading={confirming}
      >
        {confirmEntry && (
          <Box sx={{ display: 'grid', gap: 1.5, mt: 2 }}>
            <Box
              sx={{
                display: 'grid',
                gap: 1.5,
                p: 1.5,
                borderRadius: sbg.radius.lg,
                bgcolor: sbg.bg.surfaceSubtle,
              }}
            >
              <PersonCell
                name={confirmEntry.employee.name}
                secondary={confirmEntry.employee.department?.name || confirmEntry.employee.teamName || undefined}
              />
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
                <TypeLabel type={confirmEntry.type} />
                <Typography variant="body1" component="span" sx={{ color: sbg.text.primary, fontVariantNumeric: 'tabular-nums' }}>
                  {formatDateRange(confirmEntry.startDate, confirmEntry.endDate)} · {formatHours(confirmEntry.hours)}
                </Typography>
              </Box>
            </Box>
            {/* Same source as the queue's truncated note, shown in full before the decision. */}
            {confirmNote && (
              <Typography variant="body2" component="p" sx={{ color: sbg.text.secondary, overflowWrap: 'anywhere' }}>
                Note: {confirmNote}
              </Typography>
            )}
          </Box>
        )}
      </ConfirmDialog>

      {/* ── Result toast ── */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={snackbar.severity === 'success' ? 5000 : null}
        onClose={(_, reason) => {
          if (reason === 'clickaway') return;
          closeSnackbar();
        }}
      >
        <Alert severity={snackbar.severity} onClose={closeSnackbar}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default ManagerDashboard;
